import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from 'src/@/components/ui/input';
import { Switch } from 'src/@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/@/components/ui/table';
import Iconify from 'src/components/Iconify';
import { CRM_TAG_COLOR_OPTIONS, getCrmTagColor } from 'src/utils/crmTagColors';
import { FUNIL_DESIGN as D } from './FunilComponent.design';

import v2_actionListLeadStatus from 'src/actions/v2/crm/v2_actionListLeadStatus';
import v2_actionAddLeadStatus from 'src/actions/v2/crm/v2_actionAddLeadStatus';
import v2_actionEditLeadStatus from 'src/actions/v2/crm/v2_actionEditLeadStatus';
import v2_actionDeleteLeadStatus from 'src/actions/v2/crm/v2_actionDeleteLeadStatus';

const EMPTY = { _id: null, nome: '', cor_hex: 'blue', tipo: 'em_andamento', posicao: 0, padrao: false };

export default function TabLeadStatus({ cliente, usuarioId }) {
  const { t } = useTranslation('settings');

  const TIPOS = [
    { value: 'em_andamento', label: t('funnel.leadStatus.typeOngoing', 'Em andamento') },
    { value: 'ganho', label: t('funnel.leadStatus.typeWon', 'Ganho') },
    { value: 'perda', label: t('funnel.leadStatus.typeLost', 'Perda') },
  ];

  const [lista, setLista] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [salvando, setSalvando] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    buscarStatus();
  }, []);

  async function buscarStatus() {
    try {
      const result = await v2_actionListLeadStatus(cliente._id);
      if (result.status === 200 && !result.data?.error) {
        setLista(result.data.data || []);
      }
    } catch (error) {
      console.log(error);
    }
  }

  async function handleSalvar() {
    if (!form.nome?.trim()) return;
    try {
      setSalvando(true);
      const scope = {
        nome: form.nome.trim(),
        cor_hex: form.cor_hex,
        tipo: form.tipo,
        posicao: Number(form.posicao) || 0,
        padrao: Boolean(form.padrao),
        criado_por: usuarioId || null,
      };
      if (form._id) {
        await v2_actionEditLeadStatus(form._id, { scope });
      } else {
        await v2_actionAddLeadStatus(cliente._id, { scope });
      }
      await buscarStatus();
      setOpen(false);
      setForm(EMPTY);
    } catch (error) {
      console.log(error);
    } finally {
      setSalvando(false);
    }
  }

  async function handleDeletar() {
    try {
      await v2_actionDeleteLeadStatus(confirmDelete._id);
      await buscarStatus();
      setConfirmDelete(null);
    } catch (error) {
      console.log(error);
      setConfirmDelete(null);
    }
  }

  const corSelecionada = getCrmTagColor(form.cor_hex);

  return (
    <div style={{ fontFamily: D.fontStack }}>
      <div className={D.sectionHeader}>
        <div className={D.sectionHeaderMain}>
          <h2 className={D.sectionHeaderTitle}>
            {t('funnel.leadStatus.sectionTitle', 'Status de Venda')}
          </h2>
          <p className={D.sectionHeaderSub}>
            {t('funnel.leadStatus.sectionHint', 'Marca em que ponto a venda está, em paralelo ao estágio do funil')}
          </p>
        </div>
        <button
          type="button"
          className={D.primaryButton}
          onClick={() => {
            setForm(EMPTY);
            setOpen(true);
          }}
        >
          {t('buttons.create', { ns: 'common' })}
        </button>
      </div>

      <div className={D.cardFlush}>
        {lista.length === 0 ? (
          <div className={D.emptyState}>
            <p className={D.emptyDesc}>
              {t('funnel.leadStatus.emptyState', 'Nenhum status de venda foi cadastrado')}
            </p>
          </div>
        ) : (
          <div className={D.tableWrap}>
            <Table>
            <TableHeader>
              <TableRow className="border-b border-[#f0f0f3] hover:bg-transparent">
                <TableHead className={D.tableHead}>{t('labels.name', { ns: 'common' })}</TableHead>
                <TableHead className={D.tableHead}>{t('funnel.leadStatus.typeColumn', 'Tipo')}</TableHead>
                <TableHead className={`${D.tableHead} text-right`}>{t('buttons.actions', { ns: 'common' })}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lista.map((status) => {
                const cor = getCrmTagColor(status.cor_hex);
                return (
                  <TableRow key={status._id} className={D.tableRow}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-[13px] font-semibold ${cor.pill}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${cor.dot}`} />
                          {status.nome}
                        </span>
                        {status.padrao && (
                          <span className={`${D.badge} ${D.badgeNeutral}`}>
                            {t('funnel.leadStatus.defaultBadge', 'Padrão')}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className={D.tableCellMuted}>
                      {TIPOS.find((tp) => tp.value === status.tipo)?.label || status.tipo}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1.5">
                        <button
                          type="button"
                          className={D.iconButton}
                          onClick={() => {
                            setForm({
                              _id: status._id,
                              nome: status.nome,
                              cor_hex: status.cor_hex || 'blue',
                              tipo: status.tipo || 'em_andamento',
                              posicao: status.posicao ?? 0,
                              padrao: Boolean(status.padrao),
                            });
                            setOpen(true);
                          }}
                        >
                          <Iconify icon="eva:edit-2-outline" width={14} />
                        </button>
                        <button
                          type="button"
                          className={D.iconButtonDanger}
                          onClick={() => setConfirmDelete(status)}
                        >
                          <Iconify icon="eva:trash-2-outline" width={14} />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[425px]" style={{ fontFamily: D.fontStack }}>
          <DialogHeader>
            <DialogTitle className={D.dialogTitle}>
              {form._id
                ? t('funnel.leadStatus.editTitle', 'Editar status de venda')
                : t('funnel.leadStatus.createTitle', 'Novo status de venda')}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-5 py-2">
            <div className={D.field}>
              <label className={D.label}>{t('labels.name', { ns: 'common' })}</label>
              <Input
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                className={D.input}
              />
            </div>
            <div className={D.field}>
              <label className={D.label}>{t('funnel.leadStatus.typeColumn', 'Tipo')}</label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger className={D.select}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS.map((tp) => (
                    <SelectItem key={tp.value} value={tp.value}>{tp.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className={D.field}>
              <label className={D.label}>{t('tags.modal.colorLabel', 'Cor')}</label>
              <Select value={form.cor_hex} onValueChange={(v) => setForm({ ...form, cor_hex: v })}>
                <SelectTrigger className={D.select}>
                  <SelectValue>
                    <div className="flex items-center gap-2">
                      <div className={`w-5 h-5 rounded border border-[#d4d4d8] ${corSelecionada.swatch}`} />
                      <span>{corSelecionada.nome}</span>
                    </div>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {CRM_TAG_COLOR_OPTIONS.map((cor) => (
                    <SelectItem key={cor.value} value={cor.value}>
                      <div className="flex items-center gap-2">
                        <div className={`w-5 h-5 rounded border border-[#d4d4d8] flex-shrink-0 ${cor.swatch}`} />
                        <span>{cor.nome}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className={D.field}>
              <label className={D.label}>{t('funnel.leadStatus.orderLabel', 'Ordem')}</label>
              <Input
                type="number"
                value={form.posicao}
                onChange={(e) => setForm({ ...form, posicao: e.target.value })}
                className={D.input}
              />
              <p className={D.hint}>{t('funnel.leadStatus.orderHint', 'Define a posição na lista de status.')}</p>
            </div>
            <div className={`${D.toggleRow} border-t border-[#f0f0f3]`}>
              <div className={D.toggleRowMain}>
                <p className={D.toggleRowLabel}>
                  {t('funnel.leadStatus.defaultLabel', 'Status padrão')}
                </p>
                <p className={D.toggleRowHelp}>
                  {t('funnel.leadStatus.defaultHint', 'Aplicado automaticamente a negócios novos')}
                </p>
              </div>
              <Switch
                className={D.switch}
                checked={form.padrao}
                onCheckedChange={(v) => setForm({ ...form, padrao: v })}
              />
            </div>
          </div>
          <DialogFooter className={D.dialogFooter}>
            <button type="button" className={D.ghostButton} onClick={() => setOpen(false)}>
              {t('buttons.cancel', { ns: 'common' })}
            </button>
            <button
              type="button"
              className={D.primaryButton}
              onClick={handleSalvar}
              disabled={salvando || !form.nome?.trim()}
            >
              {form._id ? t('buttons.save', { ns: 'common' }) : t('buttons.create', { ns: 'common' })}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(confirmDelete)} onOpenChange={() => setConfirmDelete(null)}>
        <DialogContent className="sm:max-w-[425px]" style={{ fontFamily: D.fontStack }}>
          <DialogHeader>
            <DialogTitle className={D.dialogTitle}>{t('funnel.leadStatus.deleteTitle', 'Excluir status')}</DialogTitle>
          </DialogHeader>
          <p className={D.dialogDesc}>
            {t('funnel.leadStatus.deleteHint', 'Os negócios que usam este status ficarão sem status. Essa ação não pode ser desfeita.')}
          </p>
          <DialogFooter className={D.dialogFooter}>
            <button type="button" className={D.ghostButton} onClick={() => setConfirmDelete(null)}>
              {t('buttons.cancel', { ns: 'common' })}
            </button>
            <button type="button" className={D.dangerButton} onClick={handleDeletar}>
              {t('buttons.delete', { ns: 'common' })}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
